<script setup lang="ts">
import { useSingleRefComposable } from '@src/application/composables/single-ref-composable.js';
import { useRouterComposable } from '@router/router-composable.ts';

const contexts = ['large'];
import { computed, onMounted, provide, ref, watch } from 'vue';

const props = withDefaults(defineProps<{
    ignoreRouteParam?: boolean,
    defaultTab?: string|null,
    context?: string|null,
}>(), {
    defaultTab: null,
    context: null,
});

const ignoreRouteParam = computed<boolean>(() => {
    return props.ignoreRouteParam ?? false;
});

const { singleRef, setSingleTemplateRef } = useSingleRefComposable();

provide('headerRef', singleRef);
provide('context', props.context);
provide('ignoreRouteParam', ignoreRouteParam);

const { currentRoute } = useRouterComposable();

const activeTab = ref<string|null>(null);

onMounted(() => {
    activeTab.value = getActiveTab();
    if (props.context !== null && !contexts.includes(props.context)) {
        console.error(props.context + ': unknown context');
    }
});

provide('activeTab', activeTab);

watch(activeTab, () => {
    if (activeTab.value !== null) {
        return;
    }
    activeTab.value = getActiveTab();
});

function getActiveTab(): string|null {
    if (ignoreRouteParam.value) {
        return props.defaultTab;
    }

    // falls der Tab als Routen-Parameter in der URL angegeben wurde
    if (currentRoute.value.params!.tab !== '' && currentRoute.value.params!.tab !== undefined) {
        return currentRoute.value.params!.tab as string;
    }

    // falls der Tab als Search-Parameter in der URL angegeben wurde
    if (currentRoute.value.query) {
        for (const [key, value] of currentRoute.value.query.entries()) {
            if (key !== 'tab') {
                continue;
            }

            return value;
        }
    }

    return props.defaultTab;
}

defineExpose({
    activeTab,
});
</script>

<template>
    <div :class="$style.base_tab__wrapper">
        <div :ref="setSingleTemplateRef" :class="[$style.base_tab_label__wrapper, context !== null ? $style[context] : $style.default]"/>
        <slot/>
    </div>
</template>

<style lang="scss" module>
@use '@borders' as *;

.base_tab__wrapper {
    width: 100%;
}
.base_tab_label__wrapper {
    display: flex;

    &.default {
        margin-bottom: 1em;
        border-bottom: $thin-line-grey;
    }
    &.large {
        margin-bottom: 2em;
    }
}
</style>
